import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core'

import {
  IRoleplayAssessmentDisplay,
  RoleplayAssessmentData,
} from '../../../../services/roleplay/roleplay-assessment.types'
import { scoreToHslBackground } from '../../../../services/roleplay/addon/roleplay-assessment-color.utils'

/**
 * セッション画面用シンプル評価表示アドオンコンポーネント。
 *
 * 画面上部にsticky固定表示され、左右分割レイアウトで
 * assessment種別ラベルとreason（評価理由）を表示する。
 * score値に基づくHSL連続グラデーション背景色と
 * フェードイン・フェードアウトの遷移エフェクトを提供する。
 */
@Component({
  selector: 'app-roleplay-simple-session-assessment',
  standalone: true,
  imports: [],
  templateUrl: './roleplay-simple-session-assessment.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './roleplay-simple-session-assessment.component.scss',
})
export class RoleplaySimpleSessionAssessmentComponent
  implements IRoleplayAssessmentDisplay, OnChanges
{
  @Input() assessment: RoleplayAssessmentData | undefined

  /** 表示用の評価データ（フェードエフェクト制御用） */
  displayAssessment: RoleplayAssessmentData | undefined

  /** フェード状態（テンプレートのCSSクラスバインディング用） */
  fadeState: 'hidden' | 'fade-in' | 'visible' | 'fade-out' = 'hidden'

  /** 次に表示するデータ（fade-out中に保持） */
  private pendingAssessment: RoleplayAssessmentData | undefined

  /** 背景色CSS文字列 */
  get bgColor(): string {
    return scoreToHslBackground(this.displayAssessment?.score)
  }

  /**
   * assessment Inputの変更を検知し、フェードエフェクトを制御する。
   * 初回データ受信時はフェードインのみ。
   * 更新時はフェードアウト→フェードインを実行。
   * 遷移中の新データ到着時は即座にキャンセルして新データでフェードイン。
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assessment'] == null) {
      return
    }

    const newAssessment = this.assessment

    // undefinedに変更された場合はhiddenに戻る
    if (newAssessment == null) {
      this.fadeState = 'hidden'
      this.displayAssessment = undefined
      this.pendingAssessment = undefined
      return
    }

    if (this.fadeState === 'hidden') {
      // 初回データ受信：フェードインのみ
      this.displayAssessment = newAssessment
      this.pendingAssessment = undefined
      this.fadeState = 'fade-in'
    } else if (this.fadeState === 'fade-out') {
      // fade-out遷移中に新データ受信：即座にキャンセルしてfade-in
      this.displayAssessment = newAssessment
      this.pendingAssessment = undefined
      this.fadeState = 'fade-in'
    } else {
      // visible or fade-in中：fade-outを開始し、新データを保留
      this.pendingAssessment = newAssessment
      this.fadeState = 'fade-out'
    }
  }

  /**
   * CSSトランジション完了時のハンドラ。
   * opacityプロパティのトランジション完了のみ処理する。
   */
  onTransitionEnd(event: TransitionEvent): void {
    if (event.propertyName !== 'opacity') {
      return
    }

    if (this.fadeState === 'fade-in') {
      this.fadeState = 'visible'
    } else if (this.fadeState === 'fade-out') {
      // fade-out完了：保留中のデータでfade-in開始
      this.displayAssessment = this.pendingAssessment ?? this.assessment
      this.pendingAssessment = undefined
      this.fadeState = 'fade-in'
    }
  }
}
